import { useContext, useEffect, useRef, useState } from 'react';
import Search from '../../../Assets/Modals/SearchIcon.svg';
import { getTags } from '../../../lib/utils/apiCalls';
import { useOutsideClickForTwoRef } from '../../../Hooks/useOutsideClickForTwoRef';
import TickMark from '../../../Assets/Modals/TickMark.svg';
import { InventoryAddProductFormContext } from '../../../Context/InventoryAddProductFormContext';
import { showToast } from '../../../Components/Toaster/Toaster';
import CrossSmall from '../../../Assets/CrossSmall.svg';
import Info from '../../../Assets/Info.svg';

function Tags() {
    const [tagsList, setTagsList] = useState([]);

    const searchInputBoxRef = useRef(null);
    const searchResultDropdownRef = useRef(null);

    useOutsideClickForTwoRef(searchInputBoxRef, searchResultDropdownRef, () => {
        setSearchValue('');
    });

    const { selectedTags, setSelectedTags, formErrors } = useContext(InventoryAddProductFormContext);

    const [searchValue, setSearchValue] = useState('');

    const [displayTagTooltip, setDisplayTagTooltip] = useState(false);
    const [visible, setVisible] = useState(false);

    const handleTagSearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearchKeyDown = (e) => {
        if(e.key === 'Enter') {
            if(searchValue) {
                if(selectedTags.length < 5) {
                    setSelectedTags([ ...selectedTags, searchValue]);
                    setSearchValue('');
                }
                else {
                    showToast('Max 5 tags allowed', 'warning');
                }
            }
        }
    }

    const handleTagResultDropdownItemClick = (e, tag) => {
        // Prevent the click from bubbling up and triggering outside click
        e.preventDefault();
        e.stopPropagation();

        let isSelected = selectedTags.some(selected => selected === tag);

        if(isSelected) {
            let filteredList = selectedTags.filter(item => item !== tag);
            setSelectedTags(filteredList);
        }
        else {
            if(selectedTags.length < 5) {
                setSelectedTags([ ...selectedTags, tag]);
            }
            else {
                showToast('Max 5 tags allowed', 'warning');
            }
        }
        // Don't clear searchValue here - keep the dropdown open
    }

    // Handle checkbox click separately to prevent any event issues
    const handleCheckboxClick = (e, tag) => {
        e.preventDefault();
        e.stopPropagation();
        handleTagResultDropdownItemClick(e, tag);
    }

    useEffect(() => {
        const fetchStoreTags = async () => {
            try {
                const response = await getTags();
                setTagsList(response.tags);
            }
            catch(err) {
                console.log(err);
            }
        }

        fetchStoreTags();
    }, []);

    useEffect(() => {
        if(displayTagTooltip) {
            setVisible(true);
        } else {
            setVisible(false);
        }
    }, [displayTagTooltip]);

    return (
        <div className="border border-[#EAEAEA] p-5 rounded-xl flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <h1 className='font-semibold text-[18px] leading-[26px] select-none'>Tags</h1>
                    
                    <div className="relative w-6 h-6 flex items-center justify-center">
                        <div className="w-3 h-3 flex items-center justify-center">
                            <img 
                                onMouseEnter={() => setDisplayTagTooltip(true)} 
                                onMouseLeave={() => setDisplayTagTooltip(false)} 
                                className='w-full h-full cursor-pointer' 
                                src={Info} alt="" 
                            />
                        </div>

                        {
                            displayTagTooltip && 
                            <div className={`${visible ? 'opacity-100 visible' : 'opacity-0 invisible'} transition-all delay-500 ease-in-out absolute z-10 top-6 left-[20%]`}>
                                <div 
                                    className="
                                        w-0 h-0 
                                        border-l-[8px] border-l-transparent 
                                        border-r-[8px] border-r-transparent 
                                        border-b-[10px] border-b-[#000000]
                                    "
                                ></div>
                                <div className="relative right-1/2 w-[320px] p-3 bg-[#000000] rounded-lg">
                                    <p className='font-normal text-[12px] leading-5 text-[#FFFFFF]'>Tags help group products for easy access. For example: "Under Rs1999".</p>
                                </div>
                            </div>
                        }
                    </div>
                </div>

                {
                    formErrors.tags && 
                    <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.tags}</p>
                }
            </div>

            <div ref={searchInputBoxRef} className="relative flex items-center gap-1 border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-[9px] focus-within:border-primaryColor">
                <div className="w-6 h-6 flex items-center justify-center">
                    <img src={Search} alt="" />
                </div>

                <input 
                    className='w-[calc(100%-24px)] bg-transparent outline-none placeholder:font-normal font-medium text-base placeholder:text-[#666666] text-[#121212]' 
                    id='tag-search' 
                    name='tag-search' 
                    placeholder='Press enter to separate tags'
                    type="text" 
                    value={searchValue} 
                    onChange={handleTagSearch} 
                    onKeyDown={handleSearchKeyDown}
                />

                {
                    searchValue.trim() &&
                    <div ref={searchResultDropdownRef} className="absolute z-10 top-[58px] left-0 w-full border border-[#EAEAEA] rounded-xl overflow-hidden shadow-[0px_0px_12px_0px_#0000003D]">
                        {tagsList.filter(item => item.toLowerCase().includes(searchValue.toLowerCase())).map(tag => {
                            let isSelected = selectedTags.some(selected => selected === tag);

                            return (
                                <div 
                                    key={tag} 
                                    onClick={(e) => handleTagResultDropdownItemClick(e, tag)} 
                                    className="w-full px-3 py-4 flex gap-[10px] bg-[#FFFFFF] hover:bg-[#F5F5F5] cursor-pointer"
                                >
                                    <div 
                                        onClick={(e) => handleCheckboxClick(e, tag)}
                                        className={`w-6 h-6 cursor-pointer rounded-md ${isSelected ? 'border-none bg-primaryAccent flex items-center justify-center' : 'border border-[#D1D5DB]'} `}
                                    >
                                        <img className='cursor-pointer' src={isSelected ? TickMark : undefined} alt="" />
                                    </div>

                                    <p className='font-medium select-none text-base text-[#666666]'>
                                        {tag}
                                    </p>
                                </div>
                            )
                        })}
                    </div>
                }
            </div>

            {
                selectedTags.length > 0 &&
                <div className="flex flex-wrap gap-2">
                    {selectedTags.map(tag => {
                        return (
                            <div key={tag} className="flex items-center gap-[6px] bg-[#C1FCDA] px-3 py-[6px] rounded-2xl">
                                <p className='font-medium text-sm text-[#000000DE]'>{tag}</p>

                                <button onClick={(e) => handleTagResultDropdownItemClick(e, tag)} className="outline-none w-3 h-[14px] flex items-center justify-center">
                                    <img src={CrossSmall} alt="" />
                                </button>
                            </div>
                        )
                    })}
                </div>
            }
        </div>
    );
}

export default Tags;